import React, { useState, useRef } from 'react';
import api from '../utils/api';
import { formatDate, formatTime } from '../utils/formatters';

// Drag-to-reorder editor for the Breaks page's Edit Order mode.
//
// Only planned breaks move. Every other status keeps its break # (once a break
// goes live, its number is on the stream and in TikTok order markers), so the
// planned breaks are renumbered sequentially after the highest locked number —
// the same rule PUT /api/breaks/reorder applies server-side.
//
// Dragging uses pointer events rather than HTML5 drag-and-drop so it works
// with touch as well as a mouse. On touch only the ⠿ handle starts a drag, so
// the list still scrolls normally.
//
// Like the Breaks table, the list runs newest first: `order` is top-to-bottom,
// so the top row gets the highest number and the locked break sits below.
export default function BreakOrderEditor({ breaks, onCancel, onSaved }) {
  const planned = breaks
    .filter(b => b.status === 'planned')
    .sort((a, b) => (a.break_number ?? Infinity) - (b.break_number ?? Infinity) || a.id - b.id);
  const byId = new Map(planned.map(b => [b.id, b]));
  const lockedBreaks = breaks.filter(b => b.status !== 'planned' && b.break_number != null);
  const lastLocked = lockedBreaks.reduce((max, b) => (!max || b.break_number > max.break_number ? b : max), null);
  const lockedMax = lastLocked?.break_number || 0;

  const initialOrder = planned.map(b => b.id).reverse();
  const [order, setOrder] = useState(initialOrder);
  const [dragId, setDragId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const rowRefs = useRef(new Map());

  const newNumberOf = id => lockedMax + order.length - order.indexOf(id);
  const changed = order.some(id => newNumberOf(id) !== byId.get(id)?.break_number);

  function startDrag(e, id, fromHandle) {
    if (saving) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    if (e.pointerType !== 'mouse' && !fromHandle) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setDragId(id);
  }

  function moveDrag(e) {
    if (dragId == null) return;
    const y = e.clientY;
    // Nudge the page when the pointer nears the viewport edge so long lists
    // can be dragged past the fold.
    if (y < 60) window.scrollBy(0, -12);
    else if (y > window.innerHeight - 60) window.scrollBy(0, 12);
    setOrder(prev => {
      const others = prev.filter(id => id !== dragId);
      let idx = others.length;
      for (let i = 0; i < others.length; i++) {
        const el = rowRefs.current.get(others[i]);
        if (!el) continue;
        const r = el.getBoundingClientRect();
        if (y < r.top + r.height / 2) { idx = i; break; }
      }
      const next = [...others.slice(0, idx), dragId, ...others.slice(idx)];
      return next.every((id, i) => id === prev[i]) ? prev : next;
    });
  }

  function endDrag() { setDragId(null); }

  async function save() {
    setSaving(true);
    setError('');
    try {
      const { data } = await api.put('/api/breaks/reorder', { order: [...order].reverse() });
      onSaved(data.breaks || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save the new order');
      setSaving(false);
    }
  }

  const rowBase = {
    display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
    border: '1px solid var(--border)', borderRadius: 8, marginBottom: 6,
    background: 'var(--card-bg, var(--bg-card, #fff))', userSelect: 'none',
  };

  return (
    <div>
      <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 10 }}>
        Drag planned breaks into the order they'll run, newest on top. They're
        renumbered from #{lockedMax + 1} up. Live, pending, completed and cancelled breaks keep their numbers.
      </div>

      {order.length === 0 ? (
        <div style={{ padding: '14px 12px', border: '1px dashed var(--border)', borderRadius: 8, fontSize: 13, color: 'var(--text-light)' }}>
          No planned breaks to re-order.
        </div>
      ) : order.map(id => {
        const b = byId.get(id);
        const newNum = newNumberOf(id);
        const moved = newNum !== b.break_number;
        const dragging = dragId === id;
        return (
          <div key={id}
            ref={el => { if (el) rowRefs.current.set(id, el); else rowRefs.current.delete(id); }}
            onPointerDown={e => startDrag(e, id, false)}
            onPointerMove={moveDrag}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            style={{
              ...rowBase,
              cursor: saving ? 'default' : dragging ? 'grabbing' : 'grab',
              borderColor: dragging ? 'var(--primary, #3182ce)' : 'var(--border)',
              boxShadow: dragging ? 'var(--shadow-lg, 0 4px 12px rgba(0,0,0,0.15))' : 'none',
              position: 'relative', zIndex: dragging ? 1 : 0,
            }}>
            <span
              onPointerDown={e => { e.stopPropagation(); startDrag(e, id, true); }}
              onTouchStart={e => e.stopPropagation()}
              aria-label="Drag to re-order"
              style={{ width: 18, textAlign: 'center', fontSize: 18, lineHeight: 1, color: 'var(--text-light)', touchAction: 'none', cursor: 'inherit' }}>
              ⠿
            </span>
            <span style={{ minWidth: 48 }}>
              <strong>#{newNum}</strong>
              {moved && (
                <div style={{ fontSize: 11, color: 'var(--warning, #dd6b20)' }}>was #{b.break_number ?? '--'}</div>
              )}
            </span>
            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.name}</span>
            <span style={{ fontSize: 12, color: 'var(--text-light)', whiteSpace: 'nowrap' }}>
              {formatDate(b.date)}{b.scheduled_time ? ` · ${formatTime(b.scheduled_time)}` : ''}
            </span>
          </div>
        );
      })}

      {lastLocked && (
        <div style={{ ...rowBase, opacity: 0.55, borderStyle: 'dashed', cursor: 'default' }}
          title="Not planned — its break # is locked">
          <span style={{ width: 18, textAlign: 'center' }}>🔒</span>
          <strong style={{ minWidth: 48 }}>#{lastLocked.break_number}</strong>
          <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lastLocked.name}</span>
          <span style={{ fontSize: 12, color: 'var(--text-light)' }}>{lastLocked.status}</span>
        </div>
      )}

      {error && <div style={{ color: 'var(--danger)', fontSize: 13, margin: '8px 0' }}>{error}</div>}

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
        <button className="btn btn-secondary btn-sm" onClick={onCancel} disabled={saving}>Cancel</button>
        <button className="btn btn-primary btn-sm" onClick={save} disabled={saving || !changed}>
          {saving ? 'Saving...' : 'Save Order'}
        </button>
      </div>
    </div>
  );
}
